"use client";

import { Home } from "lucide-react";
import { useEffect } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import Membership from "@/components/index/fragments/Memberhip";
import { Panel } from "@/components/ui/Panel";
import { useAppContext } from "@/context/AppContext";
import { useSessionContext } from "@/context/SessionContext";
import { consumePendingAuthMode } from "@/lib/auth-navigation";

export function LandingAuthPanel() {
  const {
    handleSubmit,
    doForgottenPassword,
    setDoForgottenPassword,
    isPendingRegistration,
  } = useSessionContext();
  const { doLogin, setDoLogin, errorMessage, isLoading } = useAppContext();

  useEffect(() => {
    const authMode = consumePendingAuthMode();

    if (authMode === "register") {
      setDoLogin(false);
      setDoForgottenPassword(false);
    } else if (authMode === "login") {
      setDoLogin(true);
      setDoForgottenPassword(false);
    }
  }, [setDoForgottenPassword, setDoLogin]);

  if (isPendingRegistration) {
    return <Membership />;
  }

  const title = doForgottenPassword
    ? "Obnova hesla"
    : doLogin
      ? "Přihlášení"
      : "Registrace";

  return (
    <Panel
      as="section"
      id="prihlaseni"
      variant="shell"
      className="mx-auto w-full max-w-md bg-white p-5 text-softiq-copy shadow-2xl sm:p-7 lg:ml-auto lg:mr-0"
    >
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="app-block-eyebrow">14 dní zdarma</p>
          <h2 className="app-block-title mt-2">{title}</h2>
        </div>
        <div className="rounded-full border border-softiq-primary bg-softiq-primary p-3 text-white">
          <Home className="size-5" aria-hidden />
        </div>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <label className="app-form-field">
          Email
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
            placeholder="vas@email.cz"
          />
        </label>

        {!doForgottenPassword ? (
          <label className="app-form-field">
            Heslo
            <input
              name="password"
              type="password"
              autoComplete={doLogin ? "current-password" : "new-password"}
              required
              className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
              placeholder="••••••••"
            />
          </label>
        ) : null}

        {!doLogin && !doForgottenPassword ? (
          <label className="app-form-field">
            Heslo znovu
            <input
              name="password-repeat"
              type="password"
              autoComplete="new-password"
              required
              className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
              placeholder="••••••••"
            />
          </label>
        ) : null}

        {errorMessage ? <Alert tone="danger">{errorMessage}</Alert> : null}

        <Button
          type="submit"
          variant="brand"
          size="form"
          loading={isLoading}
          loadingLabel="Načítání..."
          className="w-full"
        >
          {doForgottenPassword
            ? "Obnovit heslo"
            : doLogin
              ? "Přihlásit se"
              : "Vytvořit účet"}
        </Button>
      </form>

      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          size="form"
          onClick={() => setDoForgottenPassword(true)}
        >
          Zapomenuté heslo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="form"
          onClick={() => {
            setDoLogin((current) => !current);
            setDoForgottenPassword(false);
          }}
        >
          {doLogin ? "Registrace" : "Přihlášení"}
        </Button>
      </div>
    </Panel>
  );
}
